"use client";

import { useRef, useState } from "react";
import { Check, PenLine, RotateCcw } from "lucide-react";
import type { VitrineInlineEdit } from "@/lib/vitrine/types";

// Preuve d'autonomie vécue en 10 secondes : un bloc de leur future page
// d'accueil, modifiable au clic. Local au navigateur, rien n'est enregistré
// (décision 2026-09-27) ; seul le fait d'avoir modifié est tracé.

export function InlineEditDemo({
  content,
  onEdit,
  lang = "fr",
}: {
  content: VitrineInlineEdit;
  onEdit: () => void;
  lang?: "fr" | "en";
}) {
  const en = lang === "en";
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(false);
  const [version, setVersion] = useState(0);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const start = () => {
    setEditing(true);
    requestAnimationFrame(() => titleRef.current?.focus());
  };

  const editableCls = editing
    ? "rounded outline-dashed outline-1 outline-offset-4 outline-gold/70 focus:outline-2 focus:outline-gold"
    : "";

  return (
    <div className="rounded-xl border border-line bg-paper-pure">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2 text-xs text-ink/50">
        <span>{en ? "Preview: your home page" : "Aperçu : votre page d'accueil"}</span>
        {editing ? (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-paper"
          >
            <Check className="h-3 w-3" /> {en ? "Save" : "Enregistrer"}
          </button>
        ) : (
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center gap-1 rounded-full border border-gold px-3 py-1 text-ink"
          >
            <PenLine className="h-3 w-3 text-gold" /> {en ? "Edit" : "Modifier"}
          </button>
        )}
      </div>
      {/* `key` : "Revenir au texte d'origine" remonte les blocs éditables. */}
      <div key={version} className="space-y-3 px-6 py-8 text-center sm:px-10">
        <h3
          ref={titleRef}
          contentEditable={editing}
          suppressContentEditableWarning
          onInput={() => {
            setEdited(true);
            onEdit();
          }}
          className={`font-display text-2xl text-ink sm:text-3xl ${editableCls}`}
        >
          {content.titre}
        </h3>
        <p
          contentEditable={editing}
          suppressContentEditableWarning
          onInput={() => {
            setEdited(true);
            onEdit();
          }}
          className={`mx-auto max-w-xl leading-relaxed text-ink/70 ${editableCls}`}
        >
          {content.texte}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-2 text-xs text-ink/50">
        <span>
          {en
            ? edited
              ? "On your site, this change would already be live. Here, nothing is saved."
              : "Click \"Edit\", change a word: that is how you will edit your site."
            : edited
              ? "Sur votre site, cette modification serait déjà en ligne. Ici, rien n'est enregistré."
              : "Cliquez sur « Modifier », changez un mot : c'est ainsi que vous modifierez votre site."}
        </span>
        {edited && (
          <button
            type="button"
            onClick={() => {
              setEdited(false);
              setEditing(false);
              setVersion((v) => v + 1);
            }}
            className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-ink"
          >
            <RotateCcw className="h-3 w-3" /> {en ? "Back to the original text" : "Revenir au texte d'origine"}
          </button>
        )}
      </div>
    </div>
  );
}
